import styled from "styled-components";
import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import {
  ResponsiveTable,
  Table,
  TableContent,
  TableHeader,
  Th,
} from "@/components/Table";
import { SrOnly } from "@/components/Layout/Parts";
import HighlightsTableRow from "./HighlightsTableRow";
import Button from "@/components/Button";
import { ReactComponent as PlusIcon } from "@/assets/icons/plus.svg";
import HighlightsModal from "./HighlightsModal";
import { api } from "@/utils/api";
import { HighlightResource } from "@/types/resources";
import useDebounce from "@/hooks/useDebounce";
import SearchBar from "@/components/SearchBar";

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    &:nth-child(1) {
      width: 50%;
      padding-left: 24px;
    }
    &:nth-child(2) {
      width: calc(50% - 56px);
    }

    &:nth-child(3) {
      width: 56px;
      position: relative;
    }
  }
  td {
    padding: 12px;
    vertical-align: center;
  }
`;

const StyledTableHeader = styled(TableHeader)`
  justify-content: flex-end;
`;

const HighlightsTable = () => {
  const [search, setSearch] = useState("");

  const debouncedSearch = useDebounce(search, 500);

  const [showHighlightsModal, setShowHighlightsModal] = useState(false);

  const { teamId } = useParams();

  const {
    data: highlights,
    isLoading,
    isError,
  } = useQuery<HighlightResource[]>(
    [
      "script_highlights",
      { teamId, ...(!!debouncedSearch && { debouncedSearch }) },
    ],
    () =>
      api.get<never, HighlightResource[]>("/v1/production/highlights", {
        params: {
          ...(!!debouncedSearch && { "filter[search]": debouncedSearch }),
          "filter[team_id]": teamId,
        },
      })
  );

  const highlightsColors = highlights?.map((highlight) => highlight.color);

  return (
    <>
      <StyledTableHeader>
        <Button
          color="action"
          small
          onClick={() => setShowHighlightsModal(true)}
        >
          <PlusIcon />
          Add new
        </Button>
      </StyledTableHeader>
      <SearchBar value={search} setValue={setSearch} />
      <ResponsiveTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>Category Name</Th>
              <Th>Value</Th>
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              colSpan={3}
              isError={isError}
              hasData={!!highlights?.length}
            >
              {highlights?.map((highlight) => (
                <HighlightsTableRow
                  highlight={highlight}
                  key={highlight.id}
                  highlightsColors={highlightsColors}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
      <AnimatePresence>
        {showHighlightsModal && (
          <HighlightsModal
            onClose={() => setShowHighlightsModal(false)}
            highlightsColors={highlightsColors}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default HighlightsTable;
